/**
 * AccountNotesCard — Contact/Sales preferences + General Notes for AccountOverview.
 * Props: account, canEditAccount, onAccountUpdate
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { CheckCircle2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { crmAPI } from '../../../services/api';

const AccountNotesCard = ({ account, canEditAccount, onAccountUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    contact_preferences: '',
    sales_preferences: '',
    willing_to_buy_off_sheet: false,
    other_notes: '',
  });

  useEffect(() => {
    if (account) {
      setForm({
        contact_preferences: account.contact_preferences || '',
        sales_preferences: account.sales_preferences || '',
        willing_to_buy_off_sheet: account.willing_to_buy_off_sheet || false,
        other_notes: account.other_notes || '',
      });
    }
  }, [account?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancelEdit = () => {
    setForm({
      contact_preferences: account.contact_preferences || '',
      sales_preferences: account.sales_preferences || '',
      willing_to_buy_off_sheet: account.willing_to_buy_off_sheet || false,
      other_notes: account.other_notes || '',
    });
    setIsEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await crmAPI.update(account.id, form);
      toast.success('Notes updated');
      setIsEditing(false);
      if (onAccountUpdate) onAccountUpdate();
    } catch {
      toast.error('Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle>Notes</CardTitle>
        {canEditAccount && !isEditing && (
          <Button variant="ghost" size="icon" onClick={() => setIsEditing(true)} data-testid="edit-notes-btn">
            <Pencil className="w-4 h-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Contact Preferences</Label>
              <Textarea
                value={form.contact_preferences}
                onChange={e => setForm({ ...form, contact_preferences: e.target.value })}
                placeholder="How does this person prefer to be contacted?"
                rows={2}
                data-testid="contact-prefs-textarea"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Sales Preferences</Label>
              <Textarea
                value={form.sales_preferences}
                onChange={e => setForm({ ...form, sales_preferences: e.target.value })}
                placeholder="Any sales-related preferences or notes?"
                rows={2}
                data-testid="sales-prefs-textarea"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={form.willing_to_buy_off_sheet || false}
                onCheckedChange={checked => setForm({ ...form, willing_to_buy_off_sheet: checked })}
              />
              <Label className="text-sm">Willing to buy off-sheet</Label>
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">General Notes</Label>
              <Textarea
                value={form.other_notes}
                onChange={e => setForm({ ...form, other_notes: e.target.value })}
                placeholder="Any other important notes about this account?"
                rows={4}
                data-testid="other-notes-textarea"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-4 text-sm">
            <div className="p-3 bg-muted/30 rounded">
              <span className="font-medium text-muted-foreground block mb-1">Contact Preferences:</span>
              <p>{account.contact_preferences || 'None specified'}</p>
            </div>
            <div className="p-3 bg-muted/30 rounded">
              <span className="font-medium text-muted-foreground block mb-1">Sales Preferences:</span>
              <p>{account.sales_preferences || 'None specified'}</p>
            </div>
            {account.willing_to_buy_off_sheet && (
              <div className="flex items-center gap-2 px-3">
                <CheckCircle2 className="w-4 h-4 text-primary" />
                <span className="font-medium">Willing to buy off-sheet</span>
              </div>
            )}
            <div className="p-3 bg-muted/30 rounded">
              <span className="font-medium text-muted-foreground block mb-1">General Notes:</span>
              <p className="whitespace-pre-wrap">{account.other_notes || 'None'}</p>
            </div>
          </div>
        )}
        {isEditing && (
          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
            <Button variant="outline" size="sm" onClick={cancelEdit} disabled={saving} data-testid="cancel-notes-btn">Cancel</Button>
            <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-notes-btn">{saving ? 'Saving...' : 'Save'}</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AccountNotesCard;
